import { useEffect, useMemo, type CSSProperties, type ReactNode } from "react";
import {
  Background,
  Handle,
  MarkerType,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  type Node,
  type NodeProps,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import type { ReviewProgressItem } from "@compliance-sage/contracts";
import { useI18n } from "../../../i18n";
import { progressStageMessageKey } from "../../../components/ReviewProgressDetail";
import type { SourceGroupType } from "../complianceScope/utils";
import { FlowEdge, type FlowEdgeType } from "./FlowEdge";
import { GROUP_ORDER, groupNodeId } from "./graphModel";
import { CHANNEL_EDGE_COLOR, groupAccentClass, groupEdgeColor, groupLabelKey } from "./groupPresentation";
import {
  AI_REVIEW_NODE_ID,
  AI_TO_DECISION_EDGE_ID,
  CATEGORY_NODE_ID,
  CHANNEL_NODE_ID,
  CHANNEL_TO_CATEGORY_EDGE_ID,
  DECISION_NODE_ID,
  categoryToGroupEdgeId,
  channelToGroupEdgeId,
  groupToAiEdgeId,
  sourceGroupCounts,
  stageHighlight,
  usedSourceGroups,
} from "./liveGraphModel";

const EDGE_TYPES = { flow: FlowEdge };

const CHANNEL_X = 0;
const CATEGORY_X = 196;
const GROUP_X = 420;
const AI_X = 636;
const DECISION_X = 800;
const ROW_HEIGHT = 96;
const CHANNEL_SIZE = { width: 176, height: 68 };
const CATEGORY_SIZE = { width: 200, height: 80 };
const GROUP_SIZE = { width: 192, height: 76 };
const STEP_SIZE = { width: 140, height: 64 };

interface LiveFlowState {
  active: boolean;
  flowing: boolean;
  column: number;
  glow: string;
  showArrow: boolean;
}

interface LiveChannelData extends LiveFlowState {
  [key: string]: unknown;
  label: string;
  caption: string;
}
interface LiveCategoryData extends LiveFlowState {
  [key: string]: unknown;
  label: string;
  parentName: string | null;
  caption: string;
}
interface LiveGroupData extends LiveFlowState {
  [key: string]: unknown;
  groupType: SourceGroupType;
  label: string;
  count: string;
}
interface LiveStepData extends LiveFlowState {
  [key: string]: unknown;
  label: string;
  className: string;
}

function flowVars(flow: LiveFlowState): CSSProperties {
  return {
    "--cs-flow-delay": `${flow.column * 0.22}s`,
    "--cs-flow-glow": flow.glow,
  } as CSSProperties;
}

function LiveNodeFrame({
  flow,
  className,
  children,
  handles,
}: {
  flow: LiveFlowState;
  className: string;
  children: ReactNode;
  handles: ReactNode;
}) {
  const animated = flow.flowing || flow.active;
  return (
    <div className="cs-flow-node relative h-full w-full" style={animated ? flowVars(flow) : undefined}>
      <div
        className={`relative h-full w-full overflow-hidden rounded-lg shadow-sm ${className} ${
          flow.active ? "cs-flow-node-active" : ""
        }`}
      >
        {animated ? (
          <span className={`cs-flow-node-sheen ${flow.active ? "" : "opacity-50"}`} aria-hidden="true" />
        ) : null}
        {flow.showArrow && animated ? <span className="cs-flow-node-arrow" aria-hidden="true" /> : null}
        <div className="relative">{children}</div>
      </div>
      {handles}
    </div>
  );
}

function flowHandleClass(flow: LiveFlowState, idleClass: string): string {
  return flow.active ? `cs-flow-handle ${idleClass}` : idleClass;
}

type LiveChannelFlowNode = Node<LiveChannelData, "liveChannel">;
type LiveCategoryFlowNode = Node<LiveCategoryData, "liveCategory">;
type LiveGroupFlowNode = Node<LiveGroupData, "liveGroup">;
type LiveStepFlowNode = Node<LiveStepData, "liveStep">;

function LiveChannelNode({ data }: NodeProps<LiveChannelFlowNode>) {
  return (
    <LiveNodeFrame
      flow={data}
      className={`border px-3 py-2.5 ${
        data.active ? "border-brand-500 bg-brand-50" : "border-slate-200 bg-white"
      }`}
      handles={<Handle type="source" position={Position.Right} className={flowHandleClass(data, "!bg-slate-400")} />}
    >
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{data.caption}</p>
      <p className="truncate pr-3 text-sm font-semibold text-slate-800">{data.label}</p>
    </LiveNodeFrame>
  );
}

function LiveCategoryNode({
  data,
}: NodeProps<LiveCategoryFlowNode>) {
  return (
    <LiveNodeFrame
      flow={data}
      className={`border px-3 py-2.5 ${
        data.active ? "border-brand-500 bg-white" : "border-slate-200 bg-white"
      }`}
      handles={
        <>
          <Handle type="target" position={Position.Left} className={flowHandleClass(data, "!bg-slate-400")} />
          <Handle type="source" position={Position.Right} className={flowHandleClass(data, "!bg-brand-400")} />
        </>
      }
    >
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{data.caption}</p>
      {data.parentName ? (
        <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-slate-400">{data.parentName}</p>
      ) : null}
      <p className="truncate pr-3 text-sm font-semibold text-slate-800">{data.label}</p>
    </LiveNodeFrame>
  );
}

function LiveGroupNode({
  data,
}: NodeProps<LiveGroupFlowNode>) {
  return (
    <LiveNodeFrame
      flow={data}
      className={`border-2 px-3 py-2.5 ${groupAccentClass(data.groupType)}`}
      handles={
        <>
          <Handle type="target" position={Position.Left} className={flowHandleClass(data, "!bg-slate-400")} />
          <Handle type="source" position={Position.Right} className={flowHandleClass(data, "!bg-slate-400")} />
        </>
      }
    >
      <p className="pr-3 text-sm font-semibold text-slate-800">{data.label}</p>
      <p className="mt-0.5 text-[11px] text-slate-500">{data.count}</p>
    </LiveNodeFrame>
  );
}

function LiveStepNode({
  data,
}: NodeProps<LiveStepFlowNode>) {
  return (
    <LiveNodeFrame
      flow={data}
      className={`flex items-center justify-center border-2 px-3 py-2.5 text-center text-sm font-medium ${data.className}`}
      handles={
        <>
          <Handle type="target" position={Position.Left} className={flowHandleClass(data, "!bg-slate-400")} />
          <Handle type="source" position={Position.Right} className={flowHandleClass(data, "!bg-slate-400")} />
        </>
      }
    >
      {data.label}
    </LiveNodeFrame>
  );
}

const NODE_TYPES = {
  liveChannel: LiveChannelNode,
  liveCategory: LiveCategoryNode,
  liveGroup: LiveGroupNode,
  liveStep: LiveStepNode,
};

function FitToGraph({ token }: { token: string }) {
  const { fitView } = useReactFlow();
  useEffect(() => {
    const frame = requestAnimationFrame(() => fitView({ padding: 0.24, duration: 200 }));
    return () => cancelAnimationFrame(frame);
  }, [fitView, token]);
  return null;
}

function flowEdge(
  id: string,
  source: string,
  target: string,
  color: string,
  active: boolean,
): FlowEdgeType {
  return {
    id,
    type: "flow",
    source,
    target,
    animated: active,
    data: { active, color },
    style: { stroke: color, strokeWidth: active ? 2.5 : 2, strokeDasharray: color === "#f59e0b" ? "4 4" : undefined },
    markerEnd: { type: MarkerType.ArrowClosed, color },
  };
}

export function LiveReviewGraph({ item }: { item: ReviewProgressItem }) {
  const { t } = useI18n();
  const counts = useMemo(() => sourceGroupCounts(item), [item]);
  // Always draw the same 4 groups as Sage Workflow / the system guide, even
  // when this run locked none of a group (Educate Sage at 0 items).
  const groups = GROUP_ORDER;
  const usedGroups = useMemo(() => usedSourceGroups(counts), [counts]);
  const hasCategory = Boolean(item.categoryName);
  const highlight = stageHighlight(item.progressStage, item.jobStatus, usedGroups, hasCategory);
  const flowing = highlight.decisionState === "pending";
  const midY = Math.max(0, ((groups.length - 1) * ROW_HEIGHT) / 2);
  const flowOf = (id: string, column: number, glow: string, showArrow = true): LiveFlowState => ({
    active: highlight.activeNodeIds.has(id),
    flowing,
    column,
    glow,
    showArrow,
  });

  const nodes: Node[] = useMemo(() => {
    const groupColumn = hasCategory ? 2 : 1;
    const aiColumn = groupColumn + 1;
    const decisionColumn = aiColumn + 1;
    const list: Node[] = [
      {
        id: CHANNEL_NODE_ID,
        type: "liveChannel",
        position: { x: CHANNEL_X, y: midY },
        ...CHANNEL_SIZE,
        data: {
          caption: t("scopeStepChannel"),
          label: item.channelName,
          ...flowOf(CHANNEL_NODE_ID, 0, CHANNEL_EDGE_COLOR),
        },
      },
    ];
    if (hasCategory) {
      list.push({
        id: CATEGORY_NODE_ID,
        type: "liveCategory",
        position: { x: CATEGORY_X, y: midY },
        ...CATEGORY_SIZE,
        data: {
          caption: t("scopeStepMaterial"),
          label: item.categoryName ?? "",
          parentName: item.categoryParentName,
          ...flowOf(CATEGORY_NODE_ID, 1, "#4f46e5"),
        },
      });
    }
    groups.forEach((groupType, index) => {
      list.push({
        id: groupNodeId(groupType),
        type: "liveGroup",
        position: { x: hasCategory ? GROUP_X : CATEGORY_X, y: index * ROW_HEIGHT },
        ...GROUP_SIZE,
        data: {
          groupType,
          label: t(groupLabelKey(groupType)),
          count: t("sageGroupItemCount", { count: counts[groupType] }),
          ...flowOf(groupNodeId(groupType), groupColumn, groupEdgeColor(groupType)),
        },
      });
    });
    const pipelineX = hasCategory ? AI_X : GROUP_X;
    const decisionX = hasCategory ? DECISION_X : AI_X;
    const decisionClass =
      highlight.decisionState === "success"
        ? "border-emerald-500 bg-emerald-50"
        : highlight.decisionState === "failed"
          ? "border-red-500 bg-red-50"
          : "border-slate-300 bg-white";
    list.push({
      id: AI_REVIEW_NODE_ID,
      type: "liveStep",
      position: { x: pipelineX, y: midY },
      ...STEP_SIZE,
      data: {
        label: t("sageLiveAiReview"),
        className: "border-brand-400 bg-brand-50",
        ...flowOf(AI_REVIEW_NODE_ID, aiColumn, "#4f46e5"),
      },
    });
    list.push({
      id: DECISION_NODE_ID,
      type: "liveStep",
      position: { x: decisionX, y: midY },
      ...STEP_SIZE,
      data: {
        label: t("sageLiveDecision"),
        className: decisionClass,
        ...flowOf(DECISION_NODE_ID, decisionColumn, "#4f46e5", false),
      },
    });
    return list;
  }, [counts, groups, hasCategory, highlight, item, midY, t, flowing]);

  const edges: FlowEdgeType[] = useMemo(() => {
    const list: FlowEdgeType[] = [];
    if (hasCategory) {
      list.push(
        flowEdge(
          CHANNEL_TO_CATEGORY_EDGE_ID,
          CHANNEL_NODE_ID,
          CATEGORY_NODE_ID,
          CHANNEL_EDGE_COLOR,
          highlight.activeEdgeIds.has(CHANNEL_TO_CATEGORY_EDGE_ID),
        ),
      );
    }
    for (const groupType of groups) {
      const intoId = hasCategory ? categoryToGroupEdgeId(groupType) : channelToGroupEdgeId(groupType);
      list.push(
        flowEdge(
          intoId,
          hasCategory ? CATEGORY_NODE_ID : CHANNEL_NODE_ID,
          groupNodeId(groupType),
          groupEdgeColor(groupType),
          highlight.activeEdgeIds.has(intoId),
        ),
      );
      const aiId = groupToAiEdgeId(groupType);
      list.push(
        flowEdge(
          aiId,
          groupNodeId(groupType),
          AI_REVIEW_NODE_ID,
          groupEdgeColor(groupType),
          highlight.activeEdgeIds.has(aiId),
        ),
      );
    }
    list.push(
      flowEdge(
        AI_TO_DECISION_EDGE_ID,
        AI_REVIEW_NODE_ID,
        DECISION_NODE_ID,
        "#4f46e5",
        highlight.activeEdgeIds.has(AI_TO_DECISION_EDGE_ID),
      ),
    );
    return list;
  }, [groups, hasCategory, highlight]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-800">{item.title}</p>
        <span className="text-xs font-medium text-brand-700">{t(progressStageMessageKey(item.progressStage))}</span>
      </div>
      <div className="h-[26rem] overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
        <ReactFlowProvider>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={NODE_TYPES}
            edgeTypes={EDGE_TYPES}
            fitView
            fitViewOptions={{ padding: 0.24 }}
            minZoom={0.4}
            maxZoom={1.2}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            proOptions={{ hideAttribution: true }}
          >
            <FitToGraph
              token={`${item.reviewId}:${item.progressStage}:${item.jobStatus}:${item.channelName}:${item.categoryName}:${groups.join(",")}`}
            />
            <Background />
          </ReactFlow>
        </ReactFlowProvider>
      </div>
    </div>
  );
}
